import Paper from '@/components/Paper'
import { Button } from '@/components/ui/button'
import EditIcons from '@/icons/EditIcons'
import LinkIcons from '@/icons/LinkIcons'
import { formatFromUTC } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import { AlertTriangle, ArrowRightLeft, User } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { Ticket, mapPriorityToISMS } from '../../../types/response'
import {
    getTicketUrgencyColor,
    getSecurityTagColor,
    getISMSPriorityColor,
} from '../../../types/utils'
import { ERROR_SERVICE_INCIDENT_TICKET_KEY } from '../../../constants'

interface TicketHeaderProps {
    ticket: Ticket
    canEdit: boolean
    onLinkTicket: () => void
    onConvertToProblem?: () => void
    canConvert?: boolean
    isConverting?: boolean
}

const TicketHeader: React.FC<TicketHeaderProps> = ({
    ticket,
    canEdit,
    onLinkTicket,
    onConvertToProblem,
    canConvert = false,
    isConverting = false,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const handleEdit = () => {
        const editUrl = generatePathDatacentral(paths.ticketEdit, {
            id: String(ticket?.ticket_id),
        })
        navigate(editUrl)
    }

    const isMajorIncident = ticket?.ticket_marjorable?.[0] === true

    return (
        <Paper className="!p-2 overflow-hidden border border-gray-200 rounded-sm">
            {/* Major Incident Banner */}
            {isMajorIncident && (
                <div className="bg-red-500 text-white px-4 py-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                        {t('ticket-major-incident')}
                    </span>
                </div>
            )}

            <div className="p-2">
                {/* Top Row: ID + Badges + Actions */}
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Left: Title + Type */}
                    <div className="space-y-2">
                        {/* Ticket ID and Priority */}
                        <div className="flex flex-wrap items-center gap-2">
                            <Typogaphy className="font-medium text-primary">
                                DC Ticket - {ticket?.ticket_id}
                            </Typogaphy>
                            <Chip
                                label={ticket?.ticket_priority?.toUpperCase()}
                                color={getTicketUrgencyColor(
                                    ticket?.ticket_priority || ''
                                )}
                                size="small"
                            />
                            {/* IT-ISMS P1-P4 priority badge */}
                            {ticket?.ticket_type_group === 'error_service' && (
                                <Chip
                                    label={mapPriorityToISMS(
                                        ticket?.ticket_priority,
                                        ticket?.ticket_marjorable
                                    )}
                                    color={getISMSPriorityColor(
                                        mapPriorityToISMS(
                                            ticket?.ticket_priority,
                                            ticket?.ticket_marjorable
                                        )
                                    )}
                                    size="small"
                                />
                            )}
                            {/* IT-ISMS Security tag badge */}
                            {ticket?.ticket_security_tag &&
                                ticket.ticket_security_tag !== 'none' && (
                                    <Chip
                                        label={`🔒 ${ticket.ticket_security_tag.toUpperCase()}`}
                                        color={getSecurityTagColor(
                                            ticket.ticket_security_tag
                                        )}
                                        size="small"
                                    />
                                )}
                        </div>

                        {/* Ticket Type */}
                        <Typogaphy className="text-gray-600 text-sm">
                            {ticket?.ticket_type_title}
                        </Typogaphy>
                    </div>

                    {/* Right: Actions - stack vertical on md, horizontal on lg+ */}
                    <div className="flex flex-row md:flex-col lg:flex-row items-stretch md:items-end gap-2 shrink-0">
                        {canConvert && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={onConvertToProblem}
                                disabled={isConverting}
                                className="w-full md:w-auto"
                            >
                                <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />
                                {t('ticket-convert-to-problem')}
                            </Button>
                        )}
                        <Button
                            variant={canConvert ? 'outline' : 'default'}
                            size="sm"
                            onClick={handleEdit}
                            disabled={!canEdit}
                            className="w-full md:w-auto"
                        >
                            <EditIcons className="w-3.5 h-3.5 mr-1.5" />
                            {t('edit')}
                        </Button>
                        {ticket?.ticket_type_key ===
                            ERROR_SERVICE_INCIDENT_TICKET_KEY && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onLinkTicket}
                                className="w-full md:w-auto"
                            >
                                <LinkIcons className="w-3.5 h-3.5 mr-1.5" />
                                {t('link-ticket')}
                            </Button>
                        )}
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100 my-3" />

                {/* Bottom Row: Quick Info */}
                <div className="flex justify-between">
                    <div className="flex gap-2 flex-col md:flex-row md:gap-4 uppercase">
                        {/* Customer */}
                        {ticket?.customer_name && (
                            <Link
                                to={generatePathDatacentral(
                                    paths.customerDetail,
                                    {
                                        id: ticket?.customer_id,
                                    }
                                )}
                            >
                                <div className="flex items-center gap-1.5 text-primary hover:underline">
                                    <Typogaphy className="text-xs font-medium">
                                        {ticket?.customer_id} -{' '}
                                        {ticket?.customer_name}
                                        {ticket?.company_name &&
                                            ticket?.company_name !==
                                                ticket?.customer_name && (
                                                <span className="text-gray-400">
                                                    ({ticket?.company_name})
                                                </span>
                                            )}
                                    </Typogaphy>
                                </div>
                            </Link>
                        )}

                        {ticket?.service_name && (
                            <Link
                                to={generatePathDatacentral(
                                    paths.serviceDetail,
                                    {
                                        id: ticket?.service_id,
                                    }
                                )}
                            >
                                <div className="flex items-center gap-1.5 text-primary hover:underline">
                                    <Typogaphy className="text-xs font-medium uppercase">
                                        {' '}
                                        {ticket.service_id} -{' '}
                                        {ticket.service_name}
                                    </Typogaphy>
                                </div>
                            </Link>
                        )}

                        {ticket?.ticket_technical_assignee_names?.length >
                            0 && (
                            <div className="flex items-center gap-1.5 text-primary">
                                <User className="w-2.5 h-2.5" />
                                <Typogaphy className="text-xs font-medium">
                                    {ticket?.ticket_technical_assignee_names
                                        ?.map((item) => item.employee_name)
                                        .join(', ')}
                                </Typogaphy>
                            </div>
                        )}
                    </div>

                    {/* Created Date */}
                    {ticket?.created_at && (
                        <Typogaphy className="text-xs text-muted-foreground">
                            {formatFromUTC(ticket.created_at)}
                        </Typogaphy>
                    )}
                </div>
            </div>
        </Paper>
    )
}

export default TicketHeader
